/**
 * Work-permit / immigration-reporting Q&A corpus — deterministic generator
 * (no AI credits).
 *
 * Every answer is self-contained, quotes no price, promises no outcome, and
 * defers to "ยืนยันกับหน่วยงานผู้รับ" wherever office practice varies.
 */
import { WP_REVIEWED_TH, WP_TOPICS, type WpTopic } from "./index";
import { WP_CITIES, WP_INTENTS, WP_NATIONALITIES } from "./keywords";

import { expandWave } from "../_qa/wave-expander";
import { liftFlatToFloor } from "../_qa/topup-merge";
import { WP_TOPUP_AXIS } from "../_qa/topup-axes-2";

export type WpQA = {
  slug: string;
  q: string;
  a: string;
  synonyms: string[];
  topicSlug?: string;
};

const ASOF = `ข้อมูล ณ ${WP_REVIEWED_TH} · ค่าบริการแจ้งเป็นรายเคส สอบถามทางโทรศัพท์ LINE หรืออีเมล`;

const CORE: WpQA[] = [
  {
    slug: "work-permit-vs-visa",
    q: "ใบอนุญาตทำงานกับวีซ่าต่างกันอย่างไร",
    a: `วีซ่าและการอนุญาตให้อยู่ในราชอาณาจักรอยู่ในอำนาจของสำนักงานตรวจคนเข้าเมืองตามพระราชบัญญัติคนเข้าเมือง พ.ศ. 2522 ส่วนใบอนุญาตทำงานอยู่ในอำนาจของกรมการจัดหางานตามพระราชกำหนดการบริหารจัดการการทำงานของคนต่างด้าว พ.ศ. 2560 และที่แก้ไขเพิ่มเติม ทั้งสองส่วนต้องถูกต้องพร้อมกัน มีวีซ่าถูกประเภทแต่ไม่มีใบอนุญาตทำงานจึงยังทำงานไม่ได้ ${ASOF}`,
    synonyms: ["visa กับ work permit ต่างกันไหม", "มีวีซ่าแล้วทำงานได้เลยไหม", "work permit vs visa thailand"],
  },
  {
    slug: "tm30-vs-tm47",
    q: "ตม.30 กับ ตม.47 ต่างกันอย่างไร",
    a: `ตม.30 คือการแจ้งที่พักของคนต่างด้าวซึ่งเป็นหน้าที่ของเจ้าบ้าน เจ้าของ หรือผู้ครอบครองเคหสถาน ตามพระราชบัญญัติคนเข้าเมือง พ.ศ. 2522 มาตรา 38 ส่วนตม.47 คือการที่คนต่างด้าวแจ้งที่พักอาศัยของตนเองทุก 90 วันตามมาตรา 37(5) จึงเป็นหน้าที่ของคนละฝ่ายและมีรอบเวลาต่างกัน ควรดูแลทั้งสองอย่างให้เป็นปัจจุบัน ${ASOF}`,
    synonyms: ["แจ้งที่พักกับรายงานตัว 90 วันต่างกันไหม", "tm30 tm47 difference"],
  },
  {
    slug: "start-work-before-permit",
    q: "เริ่มทำงานก่อนได้รับใบอนุญาตทำงานได้หรือไม่",
    a: `ไม่ควร การทำงานโดยไม่มีใบอนุญาตทำงานมีความเสี่ยงตามพระราชกำหนดการบริหารจัดการการทำงานของคนต่างด้าว พ.ศ. 2560 และที่แก้ไขเพิ่มเติม ทั้งฝ่ายคนต่างด้าวและนายจ้าง แนวทางที่ปลอดภัยคือรอให้ได้รับใบอนุญาตก่อนเริ่มปฏิบัติงาน และหากมีความจำเป็นทางธุรกิจ ควรหารือแนวทางที่ถูกต้องกับกรมการจัดหางานหรือที่ปรึกษาก่อน ${ASOF}`,
    synonyms: ["ทำงานก่อนได้ work permit ผิดไหม", "work without permit thailand risk"],
  },
  {
    slug: "forgot-reentry",
    q: "ลืมขอ Re-entry Permit ก่อนเดินทางออกนอกประเทศ จะเกิดอะไรขึ้น",
    a: `การเดินทางออกนอกราชอาณาจักรโดยไม่มี Re-entry Permit ทำให้การอนุญาตให้อยู่ต่อที่เหลืออยู่สิ้นผลตามพระราชบัญญัติคนเข้าเมือง พ.ศ. 2522 มาตรา 39 เมื่อกลับเข้ามาจึงต้องเริ่มกระบวนการขออยู่ต่อใหม่ตามเงื่อนไขที่มี ทางแก้ที่เป็นไปได้ต่างกันตามประเภทการอนุญาตเดิม จึงควรปรึกษาก่อนเดินทางกลับ ${ASOF}`,
    synonyms: ["ไม่ทำ re-entry permit แล้วบินออก", "re-entry permit ลืมทำ"],
  },
  {
    slug: "residence-cert-where",
    q: "หนังสือรับรองถิ่นที่อยู่ขอที่ไหน และหน่วยงานปลายทางรับแบบใด",
    a: `ขอได้จากสำนักงานตรวจคนเข้าเมืองพื้นที่ตามที่เปิดให้บริการ หรือจากสถานทูต/สถานกงสุลของประเทศผู้ยื่น หน่วยงานปลายทางแต่ละแห่งกำหนดเองว่ารับฉบับจากที่ใดและออกภายในกี่เดือน เช่น สำนักงานขนส่งบางแห่งกำหนดอายุเอกสารไว้ จึงควรโทรยืนยันกับหน่วยงานปลายทางก่อนยื่นคำขอ เพื่อไม่ต้องทำเอกสารซ้ำ ${ASOF}`,
    synonyms: ["certificate of residence thailand", "หนังสือรับรองที่อยู่ทำใบขับขี่"],
    topicSlug: "residence-certificate",
  },
  {
    slug: "abtc-not-work-permit",
    q: "บัตร ABTC ใช้ทำงานในเขตเศรษฐกิจปลายทางได้หรือไม่",
    a: `ไม่ได้ ABTC เป็นบัตรอำนวยความสะดวกการเดินทางเชิงธุรกิจระยะสั้นในเขตเศรษฐกิจเอเปคที่ให้การอนุมัติล่วงหน้า ไม่ใช่ใบอนุญาตทำงาน หากต้องปฏิบัติงานในเขตเศรษฐกิจใด ต้องขออนุญาตทำงานตามกฎหมายของที่นั้นแยกต่างหาก และการอนุมัติเป็นรายเขตเศรษฐกิจ ควรตรวจรหัสด้านหลังบัตรก่อนเดินทางทุกครั้ง ${ASOF}`,
    synonyms: ["abtc ทำงานได้ไหม", "apec card work permit"],
    topicSlug: "abtc",
  },
  {
    slug: "change-passport-notify",
    q: "เปลี่ยนเล่มหนังสือเดินทางแล้วต้องแจ้งหน่วยงานใด",
    a: `โดยทั่วไปต้องนำเล่มเดิมและเล่มใหม่ไปแสดงต่อสำนักงานตรวจคนเข้าเมืองเพื่อดำเนินการเกี่ยวกับตราประทับและข้อมูลการอนุญาตให้อยู่ และตรวจกับกรมการจัดหางานว่าต้องแก้ไขข้อมูลในใบอนุญาตทำงานหรือไม่ ขั้นตอนต่างกันตามสำนักงาน จึงควรยืนยันรายการเอกสารกับสำนักงานที่จะยื่นก่อนเดินทางไป ${ASOF}`,
    synonyms: ["พาสปอร์ตเล่มใหม่ต้องแจ้งตม.ไหม", "new passport work permit update"],
  },
  {
    slug: "translation-needed",
    q: "เอกสารต่างประเทศที่ใช้ยื่นเรื่องใบอนุญาตทำงานต้องแปลและรับรองแบบไหน",
    a: `เอกสารที่ออกในต่างประเทศ เช่น วุฒิการศึกษา ใบรับรองประสบการณ์ หรือทะเบียนสมรส มักต้องผ่านการรับรองในประเทศต้นทาง แล้วแปลเป็นภาษาไทยและผ่านนิติกรณ์ของกรมการกงสุลตามที่หน่วยงานผู้รับกำหนด เส้นทางที่ถูกต้องขึ้นกับประเทศต้นทางและหน่วยงานปลายทาง จึงควรยืนยันก่อนเริ่มแปล ${ASOF}`,
    synonyms: ["แปลวุฒิยื่น work permit", "เอกสารต่างประเทศต้องนิติกรณ์ไหม"],
  },
];

function topicQA(t: WpTopic): WpQA[] {
  const out: WpQA[] = [
    {
      slug: `${t.slug}-overview`,
      q: `${t.th} ทำอย่างไร`,
      a: `${t.summaryTh} หน่วยงานผู้รับหลักคือ ${t.authorityTh} ${ASOF}`,
      synonyms: [`${t.th} คืออะไร`, `${t.en.toLowerCase()} thailand`, `ขั้นตอน${t.th}`],
      topicSlug: t.slug,
    },
    {
      slug: `${t.slug}-documents`,
      q: `${t.th} ต้องเตรียมเอกสารอะไรบ้าง`,
      a: `เอกสารที่มักต้องใช้: ${t.requirementsTh.join(" · ")} รายการอาจต่างกันตามสำนักงานที่ยื่น จึงควรยืนยันกับหน่วยงานผู้รับก่อนวันนัด ${ASOF}`,
      synonyms: [`${t.th} เอกสารที่ต้องใช้`, `เตรียมเอกสาร${t.th}`],
      topicSlug: t.slug,
    },
    {
      slug: `${t.slug}-steps`,
      q: `ขั้นตอน${t.th} มีกี่ขั้น`,
      a: `${t.stepsTh.map((s, i) => `${i + 1}) ${s.title} — ${s.detail}`).join(" ")} ${ASOF}`,
      synonyms: [`${t.th} ขั้นตอนละเอียด`, `${t.en.toLowerCase()} process`],
      topicSlug: t.slug,
    },
    {
      slug: `${t.slug}-channels`,
      q: `${t.th} ยื่นได้ที่ช่องทางใด`,
      a: `ช่องทางที่หน่วยงานเปิดให้บริการ: ${t.channelsTh.join(" · ")} ช่องทางออนไลน์และการมอบอำนาจอาจไม่เปิดทุกสำนักงาน ควรตรวจกับสำนักงานพื้นที่ที่จะยื่นก่อน ${ASOF}`,
      synonyms: [`${t.th} ยื่นที่ไหน`, `${t.th} ออนไลน์ได้ไหม`],
      topicSlug: t.slug,
    },
    {
      slug: `${t.slug}-facts`,
      q: `${t.th} มีข้อเท็จจริงสำคัญอะไรที่ต้องรู้`,
      a: `${t.factsTh.map((f) => `${f.label}: ${f.value}`).join(" · ")} ${t.citationTh} ${ASOF}`,
      synonyms: [`${t.th} สรุปข้อมูลสำคัญ`, `${t.th} หน่วยงานไหนรับผิดชอบ`],
      topicSlug: t.slug,
    },
    {
      slug: `${t.slug}-tips`,
      q: `มีคำแนะนำอะไรก่อนยื่นเรื่อง${t.th}`,
      a: `คำแนะนำก่อนยื่น: ${t.tipsTh.slice(0, 6).join(" · ")} ${ASOF}`,
      synonyms: [`เตรียมตัว${t.th}`, `${t.th} ทำอย่างไรให้ผ่านเรียบร้อย`],
      topicSlug: t.slug,
    },
    {
      slug: `${t.slug}-warnings`,
      q: `${t.th} มีข้อควรระวังหรือสาเหตุถูกตีกลับอะไรบ้าง`,
      a: `ข้อควรระวังที่พบบ่อย: ${t.warningsTh.slice(0, 6).join(" · ")} ${t.citationTh} ${ASOF}`,
      synonyms: [`${t.th} ถูกตีกลับเพราะอะไร`, `${t.th} ผิดพลาดบ่อย`],
      topicSlug: t.slug,
    },
  ];

  for (const c of WP_CITIES) {
    out.push({
      slug: `${t.slug}-city-${c.slug}`,
      q: `${t.th} ที่${c.th} ดำเนินการอย่างไร`,
      a: `งาน${t.th} ในพื้นที่${c.th} ยื่นต่อสำนักงานที่มีเขตอำนาจครอบคลุมที่ตั้งสถานประกอบการหรือที่พักอาศัย แต่ละสำนักงานกำหนดวิธีนัดหมาย รอบเวลารับคำขอ และรายการเอกสารเพิ่มเติมได้เอง จึงควรยืนยันกับสำนักงานพื้นที่ก่อนเดินทางไปยื่น เราช่วยตรวจเอกสารและเดินเรื่องให้ได้ ${ASOF}`,
      synonyms: [`${t.th} ${c.th}`, `${t.en.toLowerCase()} ${c.slug}`],
      topicSlug: t.slug,
    });
  }

  for (const n of WP_NATIONALITIES) {
    out.push({
      slug: `${t.slug}-nat-${n.slug}`,
      q: `คน${n.th} ดำเนินการเรื่อง${t.th} ต่างจากสัญชาติอื่นไหม`,
      a: `หลักเกณฑ์ตามกฎหมายไทยใช้กับคนต่างด้าวเป็นการทั่วไป ไม่ได้แยกตามสัญชาติ แต่สิ่งที่ต่างกันในทางปฏิบัติคือรูปแบบเอกสารจากประเทศต้นทางและเส้นทางการรับรองเอกสารก่อนนำมาใช้ในไทย สำหรับผู้ถือหนังสือเดินทาง${n.th} (${n.en}) ควรตรวจว่าเอกสารต้นทางต้องผ่านการรับรองแบบใดก่อนแปลเป็นภาษาไทย ${ASOF}`,
      synonyms: [`${t.th} คน${n.th}`, `${t.en.toLowerCase()} ${n.en.toLowerCase()}`],
      topicSlug: t.slug,
    });
  }

  return out;
}

function intentQA(): WpQA[] {
  return WP_INTENTS.map((i) => ({
    slug: `intent-${i.slug}`,
    q: `กรณี${i.th} ต้องดำเนินการเรื่องใดกับหน่วยงานราชการ`,
    a: `สำหรับกรณี${i.th} ให้ไล่ตรวจสามชั้น คือ (1) สิทธิการอยู่ในราชอาณาจักรและการรายงานตัวตามพระราชบัญญัติคนเข้าเมือง พ.ศ. 2522 (2) สิทธิการทำงานตามพระราชกำหนดการบริหารจัดการการทำงานของคนต่างด้าว พ.ศ. 2560 และที่แก้ไขเพิ่มเติม และ (3) เอกสารยืนยันตัวตน/ที่อยู่ที่หน่วยงานปลายทางต้องการ รายการเอกสารที่แต่ละสำนักงานเรียกต่างกัน จึงควรยืนยันก่อนยื่น ${ASOF}`,
    synonyms: [`${i.th} ต้องทำอะไรบ้าง`, `${i.th} เอกสารที่ต้องใช้`],
  }));
}

function cityQA(): WpQA[] {
  return WP_CITIES.map((c) => ({
    slug: `city-${c.slug}`,
    q: `ที่${c.th} ทำเรื่องใบอนุญาตทำงานและรายงานตัวตรวจคนเข้าเมืองได้ที่ไหน`,
    a: `เรื่องใบอนุญาตทำงานยื่นต่อสำนักงานจัดหางานที่มีเขตอำนาจในพื้นที่${c.th} ส่วนการรายงานตัว 90 วัน การแจ้งที่พัก และ Re-entry Permit ยื่นต่อสำนักงานตรวจคนเข้าเมืองที่รับผิดชอบพื้นที่นั้น แต่ละสำนักงานกำหนดรอบเวลาและช่องทางนัดหมายเอง ควรยืนยันก่อนเดินทางไปยื่น ${ASOF}`,
    synonyms: [`ใบอนุญาตทำงาน ${c.th}`, `ตม. ${c.th}`, `work permit ${c.slug}`],
  }));
}

function buildAll(): WpQA[] {
  const WAVE = expandWave({ prefix: "wp", asOf: ASOF, authorityTh: "กรมการจัดหางานหรือสำนักงานตรวจคนเข้าเมืองที่มีเขตอำนาจ", clusterTh: "งานใบอนุญาตทำงานและการรายงานตัวคนเข้าเมือง", primary: [...WP_TOPICS, ...WP_NATIONALITIES], secondary: WP_CITIES, intents: WP_INTENTS });

const out: WpQA[] = [...WAVE, ...CORE];
  for (const t of WP_TOPICS) out.push(...topicQA(t));
  out.push(...intentQA(), ...cityQA());
  const seen = new Set<string>();
  return out.filter((q) => (seen.has(q.slug) ? false : (seen.add(q.slug), true)));
}

export const WP_QA_ALL: WpQA[] = buildAll();
liftFlatToFloor(WP_QA_ALL, {
  prefix: "wp-t7",
  asOf: ASOF,
  authorityTh: "กรมการจัดหางานและสำนักงานตรวจคนเข้าเมือง",
  clusterTh: "งานใบอนุญาตทำงานและเอกสารสำหรับผู้ทำงานในไทย",
  primary: WP_TOPUP_AXIS,
});

export const WP_QA_BY_SLUG: Record<string, WpQA> = Object.fromEntries(
  WP_QA_ALL.map((q) => [q.slug, q]),
);
export const WP_QA_COUNT = WP_QA_ALL.length;
